import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Loader from '../../../loaders';
import api from "../../../../services/api";
import { Button, Col, Container, FormGroup, Input, Label, Modal, Row } from 'reactstrap';
import { useFlexLayout, useRowSelect, useTable } from 'react-table';
import { TooltipButton } from '../../../inputs';

function PositionMilestoneDocumentListComponent(props) {
    const { id, title, data, onSelect, onMove, hideLabel, columnHeader, columnSubHeader, listPosition } = props;

    const columns = useMemo(() => [
        {
            Header: (
                <>
                    {columnHeader}
                    <br/><span style={{fontSize: "0.6rem", textTransform: "none", fontWeight: "100"}}>{columnSubHeader ?? " "}</span>
                </>
            ),
            accessor: 'lookupValue',
        },
    ], []);

    const defaultColumn = useMemo(
        () => ({
            minWidth: 30,
            width: 150,
            maxWidth: 200,
        }), []);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        // selectedFlatRows,
        toggleRowSelected,
    } = useTable (
        {
            columns,
            data,
            defaultColumn,
        },
        useFlexLayout,
        useRowSelect,
    );

    return (
        <>
            {!hideLabel && <label className="">{title}</label>}
            <div {...getTableProps()} className="table flex table-hover scrollable">
                <div className="thead">
                    {headerGroups.map(headerGroup => {
                        return (<div {...headerGroup.getHeaderGroupProps()} className="tr">
                            {headerGroup.headers.map((column, index) => {
                                return (<div {...column.getHeaderProps()} className="th">
                                    {column.render('Header')}
                                </div>);
                            })}
                            {/* <div className="th th-action text-right" style={{width: "77px"}}></div> */}
                        </div>);
                    }
                    )}
                </div>
                <div {...getTableBodyProps()} className="tbody">
                    {rows.map((row, i) => {
                        prepareRow(row);
                        return (
                            <div {...row.getRowProps()} className="tr">
                            {
                                listPosition === "right" &&
                                <div className="td td-action text-left" style={{width: "60px"}}>
                                    <TooltipButton
                                        id={`move_${i}`}
                                        title="Move"
                                        className="btn-icon"
                                        color="default"
                                        size="sm"
                                        type="button"
                                        outline
                                        onClick={() => {
                                            onMove(row.original)
                                        }}
                                    >
                                        <i className="fas fa-chevron-left"></i>
                                    </TooltipButton>
                                </div>
                            }
                            {
                                row.cells.map(cell => {
                                    return <div {...cell.getCellProps({
                                        onClick: cell.column.id !== "selection" ? () => toggleRowSelected(row.id) : undefined
                                    })}
                                        className={`td ${cell.column.id !== "selection" ? "cursor-pointer" : ""}`}
                                    >
                                        {cell.render('Cell')}
                                    </div>;
                                })
                            }
                            {
                                listPosition === "left" &&
                                <div className="td td-action text-right" style={{width: "60px"}}>
                                    <TooltipButton
                                        id={`move_${i}`}
                                        title="Move"
                                        className="btn-icon"
                                        color="default"
                                        size="sm"
                                        type="button"
                                        outline
                                        onClick={() => {
                                            onMove(row.original)
                                        }}
                                    >
                                        <i className="fas fa-chevron-right"></i>
                                    </TooltipButton>
                                </div>
                            }
                            </div>
                        );
                    })}
                </div>
            </div>
        </>
    );
}

export default function PositionMilestoneDocumentList(props) {
    const {
        milestoneDocumentId,
        onClose,
        onSaved
    } = props;

    const [ loading, setLoading ] = useState(false);
    const [ milestoneDocument, setMilestoneDocument ] = useState(null);
    const [ selectedPositions, setSelectedPositions ] = useState([]);
    const [ positionName, setPositionName ] = useState(null);
    const [ positions, setPositions ] = useState([]);

    const loadData = useCallback(() => {
        setMilestoneDocument(null);
        setSelectedPositions([]);

        if (!milestoneDocumentId)
            return;

        setLoading(true);

        const apiCalls = [
            api.get(`/milestonedocument/${milestoneDocumentId}`),
            api.get(`/milestonedocument/${milestoneDocumentId}/positions`)
        ]

        Promise.all(apiCalls)
            .then((responses) => {
                // console.log(responses);
                setMilestoneDocument(responses[0]?.data);
                setSelectedPositions(responses[1]?.data ?? []);
            })
            .catch(error => console.error(error))
            .finally(() => setLoading(false))
        ;
    }, [milestoneDocumentId]);

    useEffect(() => {
        loadData();
    }, [milestoneDocumentId]);

    const loadPositionData = useCallback(() => {
        setPositions([]);

        if (!positionName || positionName?.trim() === "")
            return;
        
        setLoading(true);
        api.get(`/lookup/position?filter=${positionName}`)
            .then((response) => {
                const loadedPositions = response.data?.filter(p => selectedPositions.filter(sp => sp.lookupId === p.lookupId).length === 0);
                // console.log(loadedPositions);
                setPositions(loadedPositions);
            })
            .catch(error => console.error(error))
            .finally(() => setLoading(false))
        ;
    }, [selectedPositions, positionName]);

    useEffect(() => {
        loadPositionData();
    }, [positionName]);

    const handleSave = useCallback(() => {
        if (!selectedPositions)
            return;
        
        setLoading(true);

        api.post(`/milestonedocument/positions/${milestoneDocumentId}`, JSON.stringify(selectedPositions.map(sp => sp.lookupId)))
            .then(() => {
                setLoading(false);
                onSaved();
            })
            .catch((error) => {
                console.error(error);
                setLoading(false);
            })
        ;
    }, [milestoneDocumentId, selectedPositions, onSaved]);

    return (
        <Modal
            isOpen={true}
            className="modal-lg"
            modalClassName="db-example-modal-lg"
        >
        {
            loading && <Loader />
        }
            <div className="modal-header">
                <h5 className="modal-title">
                    Manage Position for Interview Template - {milestoneDocument?.templateName}
                </h5>
                <button
                    aria-label="Close"
                    className="close"
                    onClick={() => onClose()}
                    type="button"
                >
                    <span aria-hidden={true}><i className="fas fa-times" /></span>
                </button>
            </div>
            <div className="modal-body">
                <Container fluid>
                    <Row>
                        <Col lg={6}>
                            <Row>
                                <Col lg={12}>
                                    <FormGroup>
                                        <Label>
                                            Available Position
                                            <span className="text-danger">*</span><br/>
                                        </Label>
                                        <Input
                                            value={positionName ?? ""}
                                            onChange={(e) => setPositionName(e.target.value)}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                    <Row>
                        <Col lg={6}>
                            <PositionMilestoneDocumentListComponent
                                id="available-positions"
                                title="Available Positions"
                                hideLabel={true}
                                data={positions}
                                onSelect={() => {}}
                                onMove={(data) => {
                                    const currentPositions = [...positions];
                                    const removedPositions = currentPositions.filter(sp => sp.lookupId === data.lookupId)[0];
                                    currentPositions.splice(currentPositions.indexOf(removedPositions), 1);
                                    setPositions(currentPositions);
                                    const currentSelectedPositions = [...selectedPositions];
                                    currentSelectedPositions.push(data);
                                    setSelectedPositions(currentSelectedPositions);
                                }}
                                columnHeader="Available Positions"
                                columnSubHeader="Enter a value in the Available Position filter to display the data."
                                listPosition="left"
                            />
                        </Col>
                        <Col lg={6}>
                            <PositionMilestoneDocumentListComponent
                                id="selected-positions"
                                title="Selected Positions"
                                hideLabel={true}
                                data={selectedPositions}
                                onSelect={() => {}}
                                onMove={(data) => {
                                    const currentSelectedPositions = [...selectedPositions];
                                    const removedPositions = currentSelectedPositions.filter(sp => sp.lookupId === data.lookupId)[0];
                                    currentSelectedPositions.splice(currentSelectedPositions.indexOf(removedPositions), 1);
                                    setSelectedPositions(currentSelectedPositions);
                                    // console.log({positionName, positions, data, added: data?.lookupValue?.toLowerCase().includes(positionName?.toLowerCase())});

                                    if (!positionName || positionName.trim() === "" || !data?.lookupValue?.toLowerCase().includes(positionName?.toLowerCase()))
                                        return;

                                    const currentPositions = [...positions];
                                    currentPositions.push(data);
                                    setPositions(currentPositions);
                                }}
                                columnHeader="Selected Positions"
                                listPosition="right"
                            />
                        </Col>
                    </Row>
                </Container>
            </div>
            <div className="modal-footer">
                <Button
                    color="secondary"
                    onClick={() => onClose()}
                    type="button"
                >
                    Close
                </Button>
                <Button
                    color="info"
                    onClick={() => handleSave()}
                    type="button"
                >
                    Save Changes
                </Button>
            </div>
        </Modal>
    );
};